/**
 * NamePromptModal + NamePromptGate (KAN-name-capture)
 * ====================================================
 *
 * Post-magic-link / post-signup name capture. Shown when:
 *   - the user has a session AND a profile, AND
 *   - `user.first_name` is empty / null, AND
 *   - the user hasn't dismissed it via the Defer button this session.
 *
 * Submits to the `set_name_details` RPC (KAN-name-capture migration
 * 20260521235017), which writes first/last/display_name + the legacy
 * `name` column atomically.
 *
 * PostHog event `name_capture_submitted` fires on successful save
 * (per feedback_telemetry_everywhere.md). Defer fires
 * `name_capture_deferred` so we can measure drop-off.
 *
 * Mirrors UsernamePromptGate (KAN-328) — same modal chrome, same defer
 * flag in sessionStorage, same auth-context wiring. Mounted globally
 * inside <AppRoutes /> after the username gate so it covers every authed
 * page once the user has a confirmed handle.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, X } from 'lucide-react';
import { useAuth } from '../../contexts/SupabaseAuthContext';

const FIRST_NAME_MAX = 80;
const LAST_NAME_MAX = 120;
const DISPLAY_NAME_MAX = 80;
const SESSION_DEFER_KEY = 'aightbet:name-prompt-deferred';
// Must match the key used by UsernamePromptGate (KAN-328). We read this
// to detect whether the username gate is currently displayed — if the user
// has dismissed the username gate via Defer, its sessionStorage flag is
// set even though `username_set_explicitly` stays false in the DB. Without
// this read, the name gate would stay suppressed forever for any user who
// deferred username setup (Bug Hunter Finding 2, P2).
const USERNAME_GATE_DEFER_KEY = 'aightbet:username-prompt-deferred';

function readUsernameGateDeferred(): boolean {
  try {
    return sessionStorage.getItem(USERNAME_GATE_DEFER_KEY) === '1';
  } catch {
    return false;
  }
}

type FormErrors = {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  submit?: string;
};

function emitPosthog(event: string, props: Record<string, unknown>): void {
  try {
    const ph = (window as any).posthog;
    if (ph && typeof ph.capture === 'function') {
      ph.capture(event, props);
    }
  } catch {
    /* posthog not loaded — non-fatal */
  }
}

interface NamePromptModalProps {
  /** "first-run" = unprompted on first authed render after signup.
   *  "edit" = user invoked from Profile · Settings. Subtle copy + dismiss
   *  affordance difference. */
  mode: 'first-run' | 'edit';
  initial?: {
    first_name?: string | null;
    last_name?: string | null;
    display_name?: string | null;
  };
  onClose: () => void;
  /** Only meaningful in first-run mode. Edit hides the Defer button —
   *  user can always Cancel from Profile · Settings. */
  onDefer?: () => void;
}

export const NamePromptModal: React.FC<NamePromptModalProps> = ({
  mode,
  initial,
  onClose,
  onDefer,
}) => {
  const { setNameDetails } = useAuth();
  const [firstName, setFirstName] = useState(initial?.first_name ?? '');
  const [lastName, setLastName] = useState(initial?.last_name ?? '');
  const [displayName, setDisplayName] = useState(initial?.display_name ?? '');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Bug Hunter Finding 4 (P3): emit `name_capture_opened` exactly once
  // per modal mount so the funnel (open → submit / defer / error) is
  // measurable in PostHog. React StrictMode double-invoke is harmless —
  // the ref guard makes the emit single-shot per real mount.
  const openedEmittedRef = React.useRef(false);
  React.useEffect(() => {
    if (openedEmittedRef.current) return;
    openedEmittedRef.current = true;
    emitPosthog('name_capture_opened', { mode });
  }, [mode]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const f = firstName.trim();
    const l = lastName.trim();
    const d = displayName.trim();

    const next: FormErrors = {};
    if (!f) next.first_name = 'First name is required.';
    if (f.length > FIRST_NAME_MAX) next.first_name = `Too long (max ${FIRST_NAME_MAX}).`;
    if (l.length > LAST_NAME_MAX) next.last_name = `Too long (max ${LAST_NAME_MAX}).`;
    if (d.length > DISPLAY_NAME_MAX) next.display_name = `Too long (max ${DISPLAY_NAME_MAX}).`;
    if (Object.keys(next).length > 0) {
      setErrors(next);
      // Bug Hunter Finding 4 (P3): expose validation-error funnel signal.
      emitPosthog('name_capture_validation_error', {
        mode,
        fields: Object.keys(next),
      });
      return;
    }

    setSaving(true);
    try {
      const result = await setNameDetails({
        first_name: f,
        last_name: l || undefined,
        display_name: d || undefined,
      });
      if (result.ok) {
        emitPosthog('name_capture_submitted', {
          mode,
          has_last_name: !!l,
          has_custom_display_name: !!d && d !== f,
        });
        try {
          sessionStorage.removeItem(SESSION_DEFER_KEY);
        } catch {
          /* non-fatal */
        }
        onClose();
      } else {
        const reasonCopy: Record<string, string> = {
          first_name_required: 'First name is required.',
          too_long: 'One of the values is too long.',
          not_authenticated: 'Please sign in again.',
          user_row_not_found: 'Your account row is missing. Contact support.',
          http_error: 'Could not save. Please try again.',
          exception: 'Could not save. Please try again.',
        };
        setErrors({
          submit: reasonCopy[result.reason ?? ''] ?? 'Could not save. Please try again.',
        });
        // Bug Hunter Finding 4 (P3): expose save-error funnel signal.
        emitPosthog('name_capture_save_error', { mode, reason: result.reason ?? 'unknown' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDefer = () => {
    emitPosthog('name_capture_deferred', { mode });
    try {
      sessionStorage.setItem(SESSION_DEFER_KEY, '1');
    } catch {
      /* non-fatal */
    }
    onDefer?.();
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="name-prompt-title"
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        className="bg-surface-elevated border border-white/10 rounded-xl shadow-card max-w-md w-full p-6 relative"
      >
        {mode === 'edit' && (
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-md text-white/40 hover:text-white/80 hover:bg-white/[0.06] inline-flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}

        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
            <UserIcon className="w-5 h-5 text-brand" aria-hidden="true" />
          </div>
          <div>
            <h3
              id="name-prompt-title"
              className="text-lg font-semibold text-white"
            >
              {mode === 'first-run' ? 'What should we call you?' : 'Update your name'}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              We use your first name in emails and notifications. Last name and
              display name are optional — leave them blank and we&rsquo;ll just
              use your first name.
            </p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-3" noValidate>
          <div className="space-y-1">
            <label
              htmlFor="name-prompt-first"
              className="text-xs font-medium text-white/60 block"
            >
              First name <span className="text-signal-negative">*</span>
            </label>
            <input
              id="name-prompt-first"
              type="text"
              autoComplete="given-name"
              autoCapitalize="words"
              autoCorrect="off"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full bg-surface-base border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brand"
              placeholder="e.g. Alex"
              maxLength={FIRST_NAME_MAX}
              required
              autoFocus
              aria-invalid={errors.first_name ? true : undefined}
              aria-describedby={errors.first_name ? 'name-prompt-first-error' : undefined}
            />
            {errors.first_name && (
              <p
                id="name-prompt-first-error"
                className="text-[11px] text-signal-negative"
                role="alert"
              >
                {errors.first_name}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="name-prompt-last"
              className="text-xs font-medium text-white/60 block"
            >
              Last name <span className="text-white/30">(optional)</span>
            </label>
            <input
              id="name-prompt-last"
              type="text"
              autoComplete="family-name"
              autoCapitalize="words"
              autoCorrect="off"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full bg-surface-base border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brand"
              placeholder="e.g. Rivera"
              maxLength={LAST_NAME_MAX}
            />
            {errors.last_name && (
              <p className="text-[11px] text-signal-negative" role="alert">
                {errors.last_name}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="name-prompt-display"
              className="text-xs font-medium text-white/60 block"
            >
              Display name{' '}
              <span className="text-white/30">(defaults to your first name)</span>
            </label>
            <input
              id="name-prompt-display"
              type="text"
              autoComplete="nickname"
              autoCorrect="off"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full bg-surface-base border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brand"
              placeholder={firstName.trim() || 'e.g. Alex'}
              maxLength={DISPLAY_NAME_MAX}
            />
            {errors.display_name && (
              <p className="text-[11px] text-signal-negative" role="alert">
                {errors.display_name}
              </p>
            )}
          </div>

          {errors.submit && (
            <p className="text-xs text-signal-negative" role="alert">
              {errors.submit}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {mode === 'first-run' && (
              <button
                type="button"
                onClick={handleDefer}
                disabled={saving}
                className="btn-secondary btn-sm"
              >
                Not now
              </button>
            )}
            {mode === 'edit' && (
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="btn-secondary btn-sm"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={saving || !firstName.trim()}
              className="btn-primary btn-sm"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>

        <p className="text-[10px] text-white/30 text-center mt-4 leading-relaxed">
          AIghtBet Sports · AI powered sports analytics · 18+ · PREDICTION not PICK
        </p>
      </motion.div>
    </motion.div>
  );
};

/**
 * NamePromptGate — global mount. Drop into <AppRoutes /> once, after the
 * UsernamePromptGate so the username gate clears first.
 *
 * Fires when authed user has `first_name === null` AND has not deferred
 * this session. Stripe webhook later backfills first/last from billing
 * details when the user pays without ever opening the form — so this
 * gate is the explicit-typed path, not the only path.
 */
export const NamePromptGate: React.FC = () => {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SESSION_DEFER_KEY) === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    if (!user) {
      try {
        sessionStorage.removeItem(SESSION_DEFER_KEY);
      } catch {
        /* non-fatal */
      }
      setDeferred(false);
    }
  }, [user?.id]);

  // Wait for real profile + first_name === null. Yield to the username gate
  // ONLY while it's actually displaying — i.e. `username_set_explicitly`
  // is false AND the user hasn't dismissed the username modal via Defer.
  // Bug Hunter Finding 2 (P2): the prior check (`username_set_explicitly === false`
  // alone) suppressed this gate forever for any user who deferred username
  // setup, because the DB flag stays false until an explicit save.
  const usernameGateActive =
    user?.username_set_explicitly === false && !readUsernameGateDeferred();
  const shouldShow =
    !!user &&
    !usernameGateActive &&
    !deferred &&
    (user.first_name === null || user.first_name === '' || user.first_name === undefined);

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      <NamePromptModal
        key="first-run-name-prompt"
        mode="first-run"
        initial={{
          first_name: user!.first_name,
          last_name: user!.last_name,
          display_name: user!.display_name,
        }}
        onClose={() => setDeferred(true)}
        onDefer={() => setDeferred(true)}
      />
    </AnimatePresence>
  );
};

export default NamePromptGate;
